/**
 * money.js — the frontend half of the money contract.
 *
 * Mirrors cnerzy-std-parts-backend/src/domain/money.js. The same nine defects had
 * a UI half: `formatMoney(n)` hardcoded `currency: 'USD'` for every base-currency
 * total, the Decide tab summed raw `unitPrice` across suppliers in different
 * currencies and printed `$`, and the Audit view — the AS9120 record of what was
 * bought — did the same.
 *
 * The rule that prevents all of it: **a function that renders money takes a
 * currency, and there is no default.** `fmt(amount)` does not compile into
 * anything useful here; you must pass what currency the amount is in. That is the
 * whole design. If you find yourself without a currency to pass, the bug is
 * upstream — you are holding a number that lost its unit.
 */

/** Amount + currency. Null for a non-finite amount, so 0 stays distinct from absent. */
export function money(amount, currency) {
  if (amount == null) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return { amount: n, currency: (currency || 'USD').toUpperCase() };
}

export function isMoney(m) {
  return !!m && typeof m.amount === 'number' && typeof m.currency === 'string';
}

/**
 * Render an amount WITH its currency. `currency` is required — omitting it is the
 * mistake this module exists to make impossible, so it renders a visible marker
 * rather than silently assuming dollars.
 */
export function fmt(amount, currency, { maximumFractionDigits = 2 } = {}) {
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  if (!currency) return `${Number(amount).toFixed(maximumFractionDigits)} (?)`;
  const code = String(currency).toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits,
    }).format(Number(amount));
  } catch (_) {
    return `${code} ${Number(amount).toFixed(maximumFractionDigits)}`;
  }
}

/** Render a Money. */
export function fmtMoney(m, opts) {
  return isMoney(m) ? fmt(m.amount, m.currency, opts) : '—';
}

/**
 * Sum amounts that are all in ONE currency, reporting what was skipped.
 *
 * Returns `{ total, counted, skipped }`. Callers must surface `skipped` rather
 * than presenting a partial total as complete — the Decide tab's grand total
 * silently added EUR to USD, which is how "$7,400" came to stand for
 * EUR 3,400 + USD 4,000.
 */
export function sumInCurrency(amounts, currency) {
  const list = Array.isArray(amounts) ? amounts : [];
  let total = 0;
  let counted = 0;
  let skipped = 0;
  for (const a of list) {
    const n = Number(a);
    if (a == null || !Number.isFinite(n)) { skipped += 1; continue; }
    total += n;
    counted += 1;
  }
  return { total: counted > 0 ? money(total, currency) : null, counted, skipped };
}

/**
 * The base-currency figure for a quote-line cell, or null.
 *
 * Same rules as the backend accessor: never the unconverted amount, and the
 * legacy fallback applies only when the quote is already in the base currency.
 */
export function baseAmount(quoteLine, baseCurrency, { field = 'effectiveTotalCost' } = {}) {
  if (!quoteLine) return null;
  if (quoteLine.isConvertible === false) return null;
  const base = (baseCurrency || 'USD').toUpperCase();
  const baseField = `${field}Base`;
  if (quoteLine[baseField] != null) return money(quoteLine[baseField], base);
  const quoted = (quoteLine.currency || base).toUpperCase();
  return quoted === base ? money(quoteLine[field], base) : null;
}

/**
 * Cost to RANK a quote on.
 *
 * `preferLanded` should be the backend's own `rankedOnLandedCost` for that line —
 * it publishes the flag per line and the frontend was ignoring it, so the ★ ranked
 * ex-works while the backend ranked delivered. The star could therefore mark a
 * part that cost $1,300 more delivered, in a cell displaying its landed cost.
 */
export function rankingAmount(quoteLine, baseCurrency, { preferLanded = false } = {}) {
  // The convertibility check must come FIRST and apply to the landed branch too.
  // Without it this returned a landed figure for a quote with no exchange rate,
  // so an unconvertible quote could be marked cheapest — in a cell simultaneously
  // displaying "no EUR→USD rate — not compared". baseAmount() checks it; the
  // landed shortcut skipped it, which is worse than the bug this module replaced.
  if (!quoteLine || quoteLine.isConvertible === false) return null;
  if (preferLanded && quoteLine.landedTotalBase != null) {
    return money(quoteLine.landedTotalBase, baseCurrency);
  }
  return baseAmount(quoteLine, baseCurrency);
}

/** Lowest-cost quote in a list, ranked in base currency. Null-safe, NaN-safe. */
export function cheapestOf(quoteLines, baseCurrency, opts) {
  let best = null;
  let bestAmount = null;
  for (const q of quoteLines || []) {
    const m = rankingAmount(q, baseCurrency, opts);
    if (!isMoney(m)) continue;
    // Strict `<` with a null guard: a NaN could otherwise become `best` and then
    // win forever, since every later comparison against NaN is false.
    if (bestAmount == null || m.amount < bestAmount) {
      best = q;
      bestAmount = m.amount;
    }
  }
  return best;
}

/** Why a cell has no comparable figure, in words a buyer can act on. */
export function unconvertibleNote(quoteLine, baseCurrency) {
  if (!quoteLine) return null;
  const base = (baseCurrency || 'USD').toUpperCase();
  const quoted = (quoteLine.currency || base).toUpperCase();
  if (quoteLine.isConvertible === false || (quoted !== base && quoteLine.effectiveTotalCostBase == null)) {
    return `no ${quoted}→${base} rate`;
  }
  return null;
}
